// fevicol-inhomz CleverTap event/action catalog (web, via useEvent().pushEvent).
// Same two-event model as mera-kaam: each screen has one `*_page_viewed` and one
// `*_page_action_taken` event, and every interaction is an `event_action` value
// on the latter (cards, CTAs, tabs are never their own events). Each leaf is a
// ready-to-use { event, action } pair for useInhomzTrack():
//
//   track(inhomzEvents.contractors.SEARCH_PERFORMED, { event_value: query });
//
// `event_value` is what was searched/chosen/clicked; `placement`/`section` say
// which instance of a repeated control fired (the same action is never split
// into differently named actions). Common props (Platform, city, page_path)
// are added automatically — see _lib/eventContext.js. No userId: the web has
// no login, so useInhomzTrack sends events without the anonymous id.
//
// To add an action to an existing screen, add one entry to its action map; to
// add a screen, add a group(...) call.
function group(actionEvent, viewedEvent, actions) {
  const result = { ACTION_EVENT: actionEvent };
  if (viewedEvent) result.VIEWED = { event: viewedEvent, action: 'page_viewed' };
  for (const [key, action] of Object.entries(actions)) {
    result[key] = { event: actionEvent, action };
  }
  return result;
}

export const inhomzEvents = {
  // Header / sidebar / footer. `placement`: header | sidebar | footer
  nav: group('ie_nav_action_taken', null, {
    LOGO_CLICKED: 'logo_clicked',
    NAV_LINK_CLICKED: 'nav_link_clicked', // event_value: link label
    CHANGE_CITY_CLICKED: 'change_city_clicked',
  }),

  home: group('ie_home_page_action_taken', 'ie_home_page_viewed', {
    BROWSE_EXPERTS_CLICKED: 'browse_experts_clicked', // placement: hero | action_card | why_choose
    EXPLORE_DESIGN_IDEAS_CLICKED: 'explore_design_ideas_clicked', // placement: hero | gallery_grid | button
    SURVEY_CLICKED: 'survey_clicked', // placement: action_card
    REAL_PHOTO_TILE_CLICKED: 'real_photo_tile_clicked', // event_value: gallery tag
    WHY_CHOOSE_ARROW_CLICKED: 'why_choose_arrow_clicked', // event_value: previous | next
    FAQ_CLICKED: 'faq_clicked', // event_value: question text
  }),

  cityPopup: group('ie_city_popup_action_taken', 'ie_city_popup_viewed', {
    CONTINUE_CLICKED: 'continue_clicked', // event_value: city id
    BROWSE_IDEAS_CLICKED: 'browse_ideas_clicked',
  }),

  designIdeas: group('ie_design_ideas_page_action_taken', 'ie_design_ideas_page_viewed', {
    BACK_CLICKED: 'back_clicked',
    CATEGORY_CLICKED: 'category_clicked', // event_value: category slug opened; section: room | category
    BROWSE_EXPERTS_CLICKED: 'browse_experts_clicked', // placement: banner
  }),

  designCategory: group('ie_design_category_page_action_taken', 'ie_design_category_page_viewed', {
    BACK_CLICKED: 'back_clicked',
    IMAGE_CLICKED: 'image_clicked', // event_value: image id; section: category_feed
    CATEGORY_CLICKED: 'category_clicked', // event_value: category slug opened; section: browse_by_room
  }),

  designDetail: group('ie_design_detail_page_action_taken', 'ie_design_detail_page_viewed', {
    BACK_CLICKED: 'back_clicked',
    CHANGE_CITY_CLICKED: 'change_city_clicked',
    CONTRACTOR_CARD_CLICKED: 'contractor_card_clicked', // event_value: contractor profile id
    IMAGE_CLICKED: 'image_clicked', // event_value: image id; section: similar_ideas
    BROWSE_EXPERTS_CLICKED: 'browse_experts_clicked', // placement: banner | compact_card
  }),

  contractors: group('ie_contractors_listing_page_action_taken', 'ie_contractors_listing_page_viewed', {
    BACK_CLICKED: 'back_clicked',
    SEARCH_CLICKED: 'search_clicked',
    SEARCH_PERFORMED: 'search_performed', // event_value: debounced search text
    FILTER_CLICKED: 'filter_clicked', // filters_count already applied
    SURVEY_CLICKED: 'survey_clicked', // placement: header | banner
    CHANGE_CITY_CLICKED: 'change_city_clicked',
    CONTRACTOR_CARD_CLICKED: 'contractor_card_clicked', // event_value: contractor profile id; card_no: position in the list, from 1
  }),

  // Mobile sheet (viewed + actions) and desktop sidebar (actions only).
  // `source`: sheet | sidebar
  contractorFilters: group(
    'ie_contractors_filter_bottom_sheet_action_taken',
    'ie_contractors_filter_bottom_sheet_viewed',
    {
      OPTION_TOGGLED: 'option_toggled', // category, event_value: option, selected
      APPLY_CLICKED: 'apply_clicked', // filter_<category>: comma-joined options, filters_count
      CLEAR_CLICKED: 'clear_clicked',
    }
  ),

  // Digital profile page (/digital-profile/[slug]). Base props on every event:
  // contractor_profile_id, contractor_name.
  contractorProfile: group('ie_contractor_profile_page_action_taken', 'ie_contractor_profile_page_viewed', {
    PROFILE_IMG_CLICKED: 'profile_img_clicked', // event_value: logo_and_profile | profile
    SHARE_CLICKED: 'share_clicked', // placement: header | cta; contractor_name only (the id is on the viewed event)
    VIEW_MORE_CLICKED: 'view_more_clicked', // placement: button | stat; event_value: stat tapped (stat only)
    GALLERY_IMG_CLICKED: 'gallery_img_clicked', // event_value: image id
    FILTER_CHIP_CLICKED: 'filter_chip_clicked', // event_value: room filter chip label (e.g. Bedroom)
    GET_IN_TOUCH_CLICKED: 'get_in_touch_clicked', // placement: bottom_bar | sidebar | photo_overlay; event_value: get_in_touch | get_in_touch_again
  }),

  // Get-in-touch flow (EnquiryFlowShell): one viewed/action pair per step.
  // Base props: contractor_profile_id, contractor_name. Never send what the
  // homeowner typed (name, number, address).
  clientForm: group('ie_client_form_action_taken', 'ie_client_form_viewed', {
    BACK_CLICKED: 'back_clicked',
    CONTINUE_CLICKED: 'continue_clicked', // work_types: comma-joined; is_editing: opened from Review
  }),

  // Also opened on its own from the listing/home survey icon (context: standalone).
  projectDetailsForm: group('ie_project_details_form_action_taken', 'ie_project_details_form_viewed', {
    BACK_CLICKED: 'back_clicked',
    CONTINUE_CLICKED: 'continue_clicked', // enquiry flow "Review and send"; survey_<question>: answer
    SAVE_CLICKED: 'save_clicked', // standalone "Save"; survey_<question>: answer
    SKIP_CLICKED: 'skip_clicked',
  }),

  reviewForm: group('ie_review_form_action_taken', 'ie_review_form_viewed', {
    BACK_CLICKED: 'back_clicked',
    CLIENT_EDIT_CLICKED: 'client_edit_clicked',
    PROJECT_DETAILS_EDIT_CLICKED: 'project_details_edit_clicked',
    SUBMIT_CLICKED: 'submit_clicked', // already_connected: enquired this contractor before
  }),

  submittedScreen: group('ie_submitted_screen_action_taken', 'ie_submitted_screen_viewed', {
    BACK_TO_PROFILE_CLICKED: 'back_to_profile_clicked',
    SHOW_MORE_PROFILE_CLICKED: 'show_more_profile_clicked',
  }),
};
